import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import { useSession } from '../auth/session'
import { fetchNotifications, pageKey, useMarkRead, useUnreadCount } from './api'
import { connectLive } from './live'
import { liveUrl, newNotificationText, notificationHref, type StaffNotification } from './model'
import { NotificationPopups } from './NotificationPopups'
import { browserSupportsPush, enablePush, listenStaffPush } from './push'
import { playChime, prepareChime } from './sound'

/** Сколько строк берёт первая страница списка — её же читают карточки. */
const FIRST_PAGE_SIZE = 10

export interface NotificationsLiveProps {
  onToast: (message: string) => void
}

/**
 * Уведомления, одни на каркас: живые события, push при открытой вкладке,
 * закрепление браузера после входа и карточки в углу, когда непрочитанных
 * стало больше. Каркас держит их, пока сотрудник вошёл.
 */
export function NotificationsLive({ onToast }: NotificationsLiveProps) {
  const api = useApi()
  const client = useQueryClient()
  const { accessToken } = useSession()
  const unread = useUnreadCount(true)
  const read = useMarkRead()
  const navigate = useNavigate()
  const [popups, setPopups] = useState<StaffNotification[]>([])

  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: keys.notificationsAll })
  }, [client])

  useEffect(
    () =>
      connectLive({
        url: (token) => liveUrl(env.apiBaseUrl, token, window.location),
        token: accessToken,
        onMessage: refresh,
      }),
    [accessToken, refresh],
  )

  useEffect(() => listenStaffPush(refresh), [refresh])

  useEffect(() => prepareChime(), [])

  // После входа и при запуске с сессией закрепляем браузер заново — только
  // если разрешение уже дано: без нажатия разрешение не спрашиваем.
  useEffect(() => {
    if (!browserSupportsPush() || Notification.permission !== 'granted') return
    enablePush(api, false).catch((error: unknown) => {
      console.warn('[push] register failed', error)
    })
  }, [api])

  const firstPage = useCallback(
    () =>
      client.fetchQuery({
        queryKey: pageKey(1, FIRST_PAGE_SIZE),
        queryFn: () => fetchNotifications(api, 1, FIRST_PAGE_SIZE),
      }),
    [api, client],
  )

  const count = unread.data
  const seen = useRef<number | undefined>(undefined)
  // Что уже было в колокольчике: карточкой всплывает только новое.
  const known = useRef(new Set<number>())
  useEffect(() => {
    if (count === undefined) return
    const before = seen.current
    seen.current = count
    if (before === undefined) {
      firstPage()
        .then((page) => {
          page.items.forEach((n) => known.current.add(n.id))
        })
        .catch(() => undefined)
      return
    }
    if (count <= before) return
    // И в фоне тоже: системный звук push macOS и Chrome часто глушат.
    playChime()
    firstPage()
      .then((page) => {
        const fresh = page.items
          .filter((n) => !n.read && !known.current.has(n.id))
          .slice(0, count - before)
        page.items.forEach((n) => known.current.add(n.id))
        if (fresh.length === 0) {
          onToast(newNotificationText(page.items))
          return
        }
        setPopups((shown) => [...fresh, ...shown])
      })
      .catch(() => {
        onToast(newNotificationText([]))
      })
  }, [count, firstPage, onToast])

  function close(notification: StaffNotification) {
    setPopups((shown) => shown.filter((n) => n.id !== notification.id))
  }

  function open(notification: StaffNotification) {
    close(notification)
    // Карточка бывает только у непрочитанного.
    read.mutate([notification.id])
    void navigate(notificationHref(notification) ?? '/notifications')
  }

  return (
    <NotificationPopups
      items={popups}
      onOpen={open}
      onClose={close}
      onAll={() => {
        setPopups([])
        void navigate('/notifications')
      }}
    />
  )
}
